import sanitizeHtml, { type IOptions } from "sanitize-html";

/**
 * Rich product / supplier descriptions (HTML from external sources).
 * Scripts, event handlers, and dangerous URLs are stripped.
 */
const PRODUCT_OPTIONS: IOptions = {
  allowedTags: [
    "a",
    "abbr",
    "b",
    "blockquote",
    "br",
    "caption",
    "code",
    "div",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "i",
    "img",
    "li",
    "ol",
    "p",
    "pre",
    "small",
    "span",
    "strike",
    "strong",
    "sub",
    "sup",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
  ],
  allowedAttributes: {
    "*": ["class"],
    a: ["href", "title", "target", "rel"],
    img: ["src", "alt", "width", "height", "referrerpolicy"],
    th: ["colspan", "rowspan", "width"],
    td: ["colspan", "rowspan", "width"],
  },
  allowedSchemes: ["http", "https", "mailto"],
  allowProtocolRelative: false,
  transformTags: {
    a: sanitizeHtml.simpleTransform("a", { rel: "noopener noreferrer" }),
  },
};

export function sanitizeProductDescriptionHtml(
  html: string | null | undefined
): string {
  if (html == null || html === "") return "";
  return sanitizeHtml(String(html), PRODUCT_OPTIONS);
}

/**
 * Short plan-feature and label strings ("250 products", "<b>Unlimited</b> imports").
 *
 * Deliberately far tighter than PRODUCT_OPTIONS: these are one-line labels that
 * render on pricing and checkout screens, so inline emphasis is all they need.
 * No links, no images, no block elements — an injected link or image on a
 * checkout screen is a phishing surface, not a formatting choice.
 */
const INLINE_OPTIONS: IOptions = {
  allowedTags: [
    "b",
    "br",
    "em",
    "i",
    "s",
    "small",
    "span",
    "strike",
    "strong",
    "sub",
    "sup",
    "u",
  ],
  allowedAttributes: {
    "*": ["class"],
  },
  allowedSchemes: [],
  allowProtocolRelative: false,
};

export function sanitizeInlineHtml(html: string | null | undefined): string {
  if (html == null || html === "") return "";
  return sanitizeHtml(String(html), INLINE_OPTIONS);
}
